import { formatPersonName } from '@/utils/personName';
/**
 * CardsForSection — per-owner debit card election + DESIGN assignment
 * (Bill 2026-07-13 r2 / BACKLOG-0873).
 *
 * Each checked owner gets their own card AND their own design. Drag a design
 * from the gallery onto an owner (or click a design, then click an owner) to
 * assign it; a thumbnail of the assigned design shows on the owner. Every card
 * owner defaults to Code 101. Persisted on the product line's featureValues:
 *   - debitCardOwners : string[]  (person ids who get a card)
 *   - debitCardDesigns: { [ownerId]: cardTypeCode }
 *
 * Card art is inline SVG-free CSS (no CDN, PCB-brand gradient per code). Real
 * card images can drop in later as cardTypes data-URIs without touching this.
 */
import { useEffect, useState } from 'react';
import type { CartPersonForModal, CardDesignOption } from './AddToCartModal';
import { useConfig } from '@/contexts/ConfigContext';
// Real PCB debit card art (Collette, docs/Debit Card Designs). Bundled as
// Vite assets → served from the SWA origin (no CDN — PCB air-gap rule).
import rt66Png from '@/assets/card-designs/rt-66.png';
import dogPng from '@/assets/card-designs/dog.png';
import whiteHexPng from '@/assets/card-designs/white-hex.png';
import mainBankPng from '@/assets/card-designs/main-bank.png';

const DEFAULT_CARD_CODE = '101';

/**
 * dbo.SystemSettings cards.issueToAllOwnersByDefault (seed 252). Bill 2026-10-01, under
 * Peter's directive: "second and third (all owners) are issued debit cards for all
 * checking accounts (which also evidences the non-TIN named owner's consent)." Every
 * owner of a checking product starts CHECKED with the default design; the FSR can
 * still uncheck one. 'false' restores the old primary-only default. Risk may change
 * either way in the Admin Panel without a release.
 */
const ISSUE_TO_ALL_OWNERS_SETTING = 'cards.issueToAllOwnersByDefault';

/** Resolve a design's artwork. Prefers the admin-maintained image served by
 *  the API (design.imageUrl — BACKLOG-0873, so admin-uploaded designs render),
 *  then the bundled PNG matched by normalized name (offline/fallback safety),
 *  then undefined → gradient fallback. */
function cardImage(design: CardDesignOption): string | undefined {
  if (design.imageUrl) return design.imageUrl;
  const n = (design.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  if (n.includes('rt66') || n.includes('route66')) return rt66Png;
  if (n.includes('dog')) return dogPng;
  if (n.includes('whitehex')) return whiteHexPng;
  if (n.includes('mainbank')) return mainBankPng;
  return undefined;
}

/** Deterministic PCB-brand gradient fallback when a design has no art yet. */
function cardGradient(code: string): string {
  const palettes: Array<[string, string]> = [
    ['#2968ad', '#1a406a'], // PCB blue (theme-exempt: card art)
    ['#386732', '#24471f'], // PCB green (theme-exempt: card art)
    ['#1a406a', '#0d2340'], // deep navy (theme-exempt: card art)
    ['#5a3e85', '#3a2757'], // plum (theme-exempt: card art)
    ['#8a5a00', '#5c3c00'], // bronze (theme-exempt: card art)
    ['#0f6b6b', '#094848'], // teal (theme-exempt: card art)
  ];
  let h = 0;
  for (let i = 0; i < code.length; i += 1) h = (h * 31 + code.charCodeAt(i)) >>> 0;
  const pair = palettes[h % palettes.length]!;
  return `linear-gradient(135deg, ${pair[0]} 0%, ${pair[1]} 100%)`;
}

function CardArt({
  design, small, active, draggable, onDragStart, onClick,
}: {
  design: CardDesignOption;
  small?: boolean;
  active?: boolean;
  draggable?: boolean;
  onDragStart?: () => void;
  onClick?: () => void;
}): JSX.Element {
  const img = cardImage(design);
  // Real card art fills the tile; the chip/label overlay is shown only on the
  // gradient fallback (the printed art already carries branding).
  const style: React.CSSProperties = img
    ? { backgroundImage: `url(${img})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: cardGradient(design.code) };
  return (
    <div
      className={['rao-card-art', small ? 'rao-card-art--sm' : '', active ? 'is-active' : ''].filter(Boolean).join(' ')}
      style={style}
      draggable={draggable}
      onDragStart={draggable ? (e) => { e.dataTransfer.setData('text/plain', design.code); onDragStart?.(); } : undefined}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      aria-label={`${design.name} card design`}
      title={`${design.name} · Code ${design.code}${design.cardNetwork ? ` · ${design.cardNetwork}` : ''}`}
    >
      {!img && (
        <>
          <span className="rao-card-art__chip" aria-hidden />
          <span>
            <span className="rao-card-art__name">{design.name}</span>
            {!small && <span className="rao-card-art__net"> {design.cardNetwork ?? `Code ${design.code}`}</span>}
          </span>
        </>
      )}
    </div>
  );
}

export interface CardsForSectionProps {
  people: ReadonlyArray<CartPersonForModal>;
  primaryOwnerId: string;
  jointOwnerIds: ReadonlyArray<string>;
  eligibleCardTypes: ReadonlyArray<CardDesignOption>;
  featureValues: Record<string, unknown>;
  setFeatureValues: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  productCardCategory: 'Personal' | 'Business';
}

export function CardsForSection({
  people, primaryOwnerId, jointOwnerIds, eligibleCardTypes,
  featureValues, setFeatureValues, productCardCategory,
}: CardsForSectionProps): JSX.Element {
  const ownerIds = [primaryOwnerId, ...jointOwnerIds].filter(Boolean);
  const { config } = useConfig();
  const issueToAll = String((config.systemSettings as Record<string, unknown>)[ISSUE_TO_ALL_OWNERS_SETTING] ?? 'true').trim().toLowerCase() !== 'false';
  const defaultOwners: ReadonlyArray<string> = issueToAll ? ownerIds : [primaryOwnerId].filter(Boolean);
  // Bill 2026-10-15: with issue-to-all on, the line remembers who was UNticked (debitCardOptOut), so an
  // owner added later is a cardholder too. The stored list used to freeze at first render (primary only).
  const rawOptOut = featureValues['debitCardOptOut'];
  const optOut: ReadonlyArray<string> = Array.isArray(rawOptOut) ? (rawOptOut as string[]) : [];
  const rawOwners = featureValues['debitCardOwners'];
  const cardOwners: ReadonlyArray<string> = issueToAll
    ? ownerIds.filter((o) => !optOut.includes(o))
    : Array.isArray(rawOwners) ? (rawOwners as string[]) : defaultOwners;
  const rawDesigns = featureValues['debitCardDesigns'];
  const designs: Record<string, string> = (rawDesigns && typeof rawDesigns === 'object')
    ? { ...(rawDesigns as Record<string, string>) } : {};

  const defaultDesign = eligibleCardTypes.find((c) => c.code === DEFAULT_CARD_CODE)?.code
    ?? eligibleCardTypes[0]?.code ?? DEFAULT_CARD_CODE;

  const [activeDesign, setActiveDesign] = useState<string>(defaultDesign);
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  // Seed the owner list (when the line has none yet) and a default design for every
  // card owner that has none, so commit issues the cards even if the FSR never
  // touches this section (BACKLOG-1669).
  const ownersKey = cardOwners.join(',');
  const defaultOwnersKey = defaultOwners.join(',');
  useEffect(() => {
    setFeatureValues((prev) => {
      const cur = { ...(prev['debitCardDesigns'] as Record<string, string> ?? {}) };
      let changed = false;
      if (issueToAll) {
        // Keep the stored list = every account owner not opted out, and the opt-out list present.
        const prevOpt = Array.isArray(prev['debitCardOptOut']) ? (prev['debitCardOptOut'] as string[]) : [];
        const owners = ownerIds.filter((o) => !prevOpt.includes(o));
        for (const pid of owners) {
          if (!cur[pid]) { cur[pid] = defaultDesign; changed = true; }
        }
        const prevOwners = Array.isArray(prev['debitCardOwners']) ? (prev['debitCardOwners'] as string[]) : [];
        if (prevOwners.join(',') !== owners.join(',') || !Array.isArray(prev['debitCardOptOut'])) changed = true;
        return changed ? { ...prev, debitCardOwners: owners, debitCardOptOut: prevOpt, debitCardDesigns: cur } : prev;
      }
      const hadOwners = Array.isArray(prev['debitCardOwners']);
      const owners = hadOwners ? (prev['debitCardOwners'] as string[]) : [...defaultOwners];
      for (const pid of owners) {
        if (!cur[pid]) { cur[pid] = defaultDesign; changed = true; }
      }
      if (!hadOwners && owners.length > 0) changed = true;
      return changed ? { ...prev, debitCardOwners: owners, debitCardDesigns: cur } : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownersKey, defaultOwnersKey, defaultDesign, issueToAll, ownerIds.join(',')]);

  const setOwnerChecked = (pid: string, checked: boolean): void => {
    if (issueToAll) {
      setFeatureValues((prev) => {
        const prevOpt = Array.isArray(prev['debitCardOptOut']) ? (prev['debitCardOptOut'] as string[]) : [];
        const nextOpt = checked ? prevOpt.filter((o) => o !== pid) : [...prevOpt.filter((o) => o !== pid), pid];
        const nextDesigns = { ...(prev['debitCardDesigns'] as Record<string, string> ?? {}) };
        if (checked && !nextDesigns[pid]) nextDesigns[pid] = defaultDesign;
        return {
          ...prev,
          debitCardOptOut: nextOpt,
          debitCardOwners: ownerIds.filter((o) => !nextOpt.includes(o)),
          debitCardDesigns: nextDesigns,
        };
      });
      return;
    }
    setFeatureValues((prev) => {
      const cur = Array.isArray(prev['debitCardOwners'])
        ? (prev['debitCardOwners'] as string[]) : [...defaultOwners];
      const nextOwners = checked ? [...cur.filter((o) => o !== pid), pid] : cur.filter((o) => o !== pid);
      const nextDesigns = { ...(prev['debitCardDesigns'] as Record<string, string> ?? {}) };
      if (checked && !nextDesigns[pid]) nextDesigns[pid] = defaultDesign;
      return { ...prev, debitCardOwners: nextOwners, debitCardDesigns: nextDesigns };
    });
  };

  const assignDesign = (pid: string, code: string): void => {
    setFeatureValues((prev) => ({
      ...prev,
      debitCardDesigns: { ...(prev['debitCardDesigns'] as Record<string, string> ?? {}), [pid]: code },
    }));
  };

  const designByCode = (code: string): CardDesignOption =>
    eligibleCardTypes.find((c) => c.code === code) ?? eligibleCardTypes[0]!;

  return (
    <section className="rao-add2cart__section">
      <header className="rao-add2cart__section-h">
        DEBIT CARD DESIGN
        <span className="rao-add2cart__section-sub">
          ({productCardCategory.toLowerCase()} cards · drag a design onto an owner, or click a design then an owner)
        </span>
      </header>

      <div className="rao-card-design__gallery" role="listbox" aria-label="Card designs">
        {eligibleCardTypes.map((ct) => (
          <CardArt
            key={ct.code}
            design={ct}
            draggable
            active={ct.code === activeDesign}
            onDragStart={() => setActiveDesign(ct.code)}
            onClick={() => setActiveDesign(ct.code)}
          />
        ))}
      </div>

      <header className="rao-add2cart__section-h" style={{ marginTop: 4 }}>
        CARDS FOR
        <span className="rao-add2cart__section-sub">(each checked owner gets their own card at commit)</span>
      </header>
      <div className="rao-card-owner__row">
        {ownerIds.map((pid) => {
          const person = people.find((p) => p.id === pid);
          const name = person ? (formatPersonName(person) || pid) : pid;
          const checked = cardOwners.includes(pid);
          const assigned = designs[pid] ?? defaultDesign;
          return (
            <div
              key={pid}
              className={['rao-card-owner', dropTarget === pid ? 'is-drop' : ''].filter(Boolean).join(' ')}
              onDragOver={checked ? (e) => { e.preventDefault(); setDropTarget(pid); } : undefined}
              onDragLeave={() => setDropTarget((t) => (t === pid ? null : t))}
              onDrop={checked ? (e) => {
                e.preventDefault();
                const code = e.dataTransfer.getData('text/plain') || activeDesign;
                if (code) assignDesign(pid, code);
                setDropTarget(null);
              } : undefined}
              onClick={checked ? () => assignDesign(pid, activeDesign) : undefined}
              role={checked ? 'button' : undefined}
              title={checked ? 'Drop or click to apply the selected design' : undefined}
            >
              <input
                type="checkbox"
                checked={checked}
                aria-label={`Issue a card to ${name}`}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setOwnerChecked(pid, e.target.checked)}
              />
              {checked && <CardArt design={designByCode(assigned)} small />}
              <span className="rao-card-owner__body">
                <span className="rao-card-owner__name">{name}{pid === primaryOwnerId ? ' (primary)' : ''}</span>
                {checked && (
                  <span className="rao-card-owner__design">{designByCode(assigned).name} · Code {assigned}</span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default CardsForSection;
